import { capitalize } from '@coxy/utils'
import { selectedCurrentLanguageObject } from '@shared-core/features/settings'
import type { Locale } from '@shared-core/features/settings/configs/localizations'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import localesDay from '@shared-core/locales/day.dynamic.i18n.json'
import localesMonths from '@shared-core/locales/months.dynamic.i18n.json'
import localesWeek from '@shared-core/locales/week.dynamic.i18n.json'
import { useAppSelector } from '@shared-core/store/hooks'
import {
  addDays,
  type DateArg,
  type Locale as DateFnsLocale,
  format,
  formatRelative,
  parse,
  parseISO,
  parseJSON,
} from 'date-fns'
import { enUS, es, hi, hy, id, ja, ka, kk, km, ko, mn, ptBR, ru, th, vi } from 'date-fns/locale'

const locales: Record<Locale, DateFnsLocale> = {
  en: enUS,
  ru: ru,
  kz: kk,
  hy: hy,
  ka: ka,
  ky: ru, // todo
  hi: hi,
  th: th,
  vi: vi,
  fil: enUS,
  lo: th,
  km: km,
  mn: mn,
  ko: ko,
  ja: ja,
  'pt-br': ptBR,
  id: id,
  es: es,
  'es-ar': es,
}

export const formatDate = (date: DateArg<Date>, formatString: string, locale?: Locale) =>
  format(date, formatString, { locale: locale ? locales[locale] : undefined })

export const formatDateInputDdMmYyyy = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  const day = digits.slice(0, 2)
  const month = digits.slice(2, 4)
  const year = digits.slice(4, 8)
  return [day, month, year].filter(Boolean).join('.')
}

export const useDate = () => {
  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000

  const { t: tMonth } = useI18N(localesMonths)
  const { t: tDay } = useI18N(localesDay)

  return (ddmmyyyy: string) => {
    const [d, m, y] = ddmmyyyy.split('.').map((v) => parseInt(v, 10))
    const date = new Date(y, m - 1, d)
    const startOfDate = new Date(y, m - 1, d).getTime()

    if (startOfDate === startOfToday) return tDay('today')

    if (startOfDate === startOfYesterday) return tDay('yesterday')
    const monthKey = formatDate(date, 'MMMM').toLowerCase()
    const monthTitle = tMonth(`${monthKey}-gen` as 'january-gen')
    const yearSuffix = y !== today.getFullYear() ? ` ${y}` : ''
    return `${d} ${monthTitle}${yearSuffix}`
  }
}

export const getDateDistance = (date: string, locale: Locale) => {
  const parsed1 = parse(date, 'yyyy-MM-dd HH:mm:ss X', new Date())
  const parsed2 = parseISO(date)
  const isNaN1 = Number.isNaN(parsed1.getTime())
  const isNaN2 = Number.isNaN(parsed2.getTime())
  const isFullNan = isNaN1 && isNaN2

  if (isFullNan) return ''

  if (isNaN2) {
    return formatRelative(parsed1, new Date(), { locale: locales[locale] })
  }
  return formatRelative(parsed2, new Date(), { locale: locales[locale] })
}

export const forwardDate = (date: string, days: number) => {
  return addDays(parseJSON(date), days)
}

export const getUtcDate = (date?: DateArg<Date>) => {
  const dateOffset = date ? new Date(date) : new Date()
  return new Date(dateOffset.getTime())
}

export function useDateToFull() {
  const currentLanguageObject = useAppSelector(selectedCurrentLanguageObject)
  const { t } = useI18N(localesMonths)

  return useEvent((date: DateArg<Date>): string => {
    if (date instanceof Date && Number.isNaN(date.getTime())) {
      return ''
    }

    const utcDate = getUtcDate(date)
    const toMonths = t(`${formatDate(utcDate, 'MMMM')}-short`.toLowerCase() as 'january-short')

    if (currentLanguageObject.timeFormat === '24') {
      return formatDate(utcDate, `dd ### yyyy HH:mm`).replace('###', toMonths)
    }
    return formatDate(utcDate, `### dd, yyyy hh:mm aaa`).replace('###', toMonths)
  })
}

export function useDateWeek() {
  const { t } = useI18N(localesWeek)

  return useEvent((weekDay: Date): string => {
    return capitalize(t(formatDate(weekDay, 'ccc').toLowerCase() as 'sat'))
  })
}

type DateInput = DateArg<Date> | string | number | null | undefined
const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null

  // unix seconds support
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// Дата в деталях операции: точечный формат из макета, время — по настройке языка.
export function useTxDateTimeFormatter() {
  const { language } = useI18N({})
  const currentLanguageObject = useAppSelector(selectedCurrentLanguageObject)

  return useEvent((value: DateInput): string => {
    const date = toDate(value)

    if (!date) return ''

    const pattern = currentLanguageObject.timeFormat === '24' ? 'dd.MM.yyyy, HH:mm' : 'dd.MM.yyyy, hh:mm aaa'

    return formatDate(getUtcDate(date), pattern, language)
  })
}

export function useDateDotFormatter() {
  const { language } = useI18N({})
  const formatDateDot = useEvent((value: DateInput): string => {
    const date = toDate(value)

    if (!date) return ''
    return formatDate(getUtcDate(date), 'dd.MM.yyyy', language)
  })
  const formatDateTimeDot = useEvent((value: DateInput): string => {
    const date = toDate(value)

    if (!date) return ''
    return formatDate(getUtcDate(date), 'dd.MM.yyyy, HH:mm', language)
  })
  return {
    formatDateDot,
    formatDateTimeDot,
  }
}

export const useNotificationDate = () => {
  const humanizeDate = useDate()

  return (timestamp: string) => {
    const date = new Date(timestamp)

    if (Number.isNaN(date.getTime())) return ''

    const label = humanizeDate(formatDate(date, 'dd.MM.yyyy'))

    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const isTodayOrYesterday = startOfDate === startOfToday || startOfDate === startOfToday - 24 * 60 * 60 * 1000

    return isTodayOrYesterday ? `${label} ${formatDate(date, 'HH:mm')}` : label
  }
}
